import { CommonModule, NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormField, MatHint } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';
import { AlertComponent } from '../../shared/components/dialogs/alert/alert.component';
import { LoaderDialogComponent } from '../../shared/components/loader-dialog/loader-dialog.component';
import { BreadcrumbsOptions } from '../../shared/models/breadcrumbs-model';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { OrderForm } from '../models/order';
import { OrdersService } from '../services/orders/orders.service';

@Component({
  selector: 'app-order-summary',
  imports: [
    FlexModule,
    TranslatePipe,
    MatFormField,
    MatInput,
    MatHint,
    FormsModule,
    BreadcrumbsComponent,
    ReactiveFormsModule,
    MatTableModule,
    NgClass,
    MatError,
    CommonModule,
    MatIconModule,
    MatTooltip,
    MatButtonModule,
    MatSelectModule,
  ],
  templateUrl: './order-summary.component.html',
  styleUrl: './order-summary.component.scss'
})
export class OrderSummaryComponent implements OnInit, OnDestroy {
  totalQuantity!: number;
  cartItems: any[] = [];
  orderId?: string;
  staticProjectName: string = '';
  staticProjectType: string = '';
  staticProjectCostCenter: string = '';

  summaryForm: FormControl = new FormControl('');
  costCenter: FormControl = new FormControl('', Validators.required);
  projectDescription: FormControl = new FormControl('', Validators.required);
  projectType: FormControl = new FormControl('', Validators.required);

  breadcrumbsData: BreadcrumbsOptions = {
    title: 'general.order-summary',
    buttons: [
      {
        label: 'button.back',
        style: 'blue',
        router: undefined
      },
      {
        label: 'button.confirm-request',
        style: 'green',
        do: () => {
          this.confirmOrderRequest();
        },
        disabled: true
      }
    ]
  };

  displayedColumns: string[] = ['codItem', 'partNumber', 'item', 'kit', 'category', 'manufacturer', 'quantity', 'action'];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private router: Router,
    private ordersService: OrdersService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initializeComponent(): void {
    const state = history.state as { cartItems?: any[], totalQuantity?: number };
    const queryParams = this.router.parseUrl(this.router.url).queryParams;

    this.orderId = queryParams['orderId'];

    this.staticProjectName = localStorage.getItem('originalProjectDescription') || '';
    this.staticProjectType = localStorage.getItem('originalProjectType') || '';
    this.staticProjectCostCenter = localStorage.getItem('originalProjectCostCenter') || '';

    this.loadFormValues();
    this.loadOrderData(state);
    this.configureBreadcrumbs();
    this.setupFormListeners();
    this.updateConfirmButtonState();
  }

  private updateConfirmButtonState(): void {
    if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons[1]) {
      const validQuantities = this.cartItems.length > 0 && this.cartItems.every(item => item.requestedQuantity >= 1);
      this.breadcrumbsData.buttons[1].disabled = !(this.projectDescription.valid && this.projectType.valid && validQuantities && this.costCenter.valid);
    }
  }

  private loadFormValues(): void {
    const projectDescription = localStorage.getItem('projectDescription');
    const remarks = localStorage.getItem('remarks');
    const projectType = localStorage.getItem('projectType');
    const costCenter = localStorage.getItem('originalProjectCostCenter');

    if (projectDescription) {
      this.projectDescription.setValue(projectDescription);
    }
    if (projectType) {
      this.projectType.setValue(projectType);
    }
    if (remarks) {
      this.summaryForm.setValue(remarks);
    }
    if (costCenter){
      this.costCenter.setValue(costCenter);
    }
  }

  private loadOrderData(state: { cartItems?: any[], totalQuantity?: number }): void {
    const storedCartItems = localStorage.getItem('cartItems');
    const storedTotalQuantity = localStorage.getItem('totalQuantity');
    if (storedCartItems) {
      this.cartItems = JSON.parse(storedCartItems);
      this.totalQuantity = storedTotalQuantity ? Number(storedTotalQuantity) : 0;
    } else if (state && state.cartItems) {
      this.cartItems = state.cartItems;
      this.totalQuantity = state.totalQuantity ?? 0;
    } else {
      this.cartItems = [];
      this.totalQuantity = 0;
    }
  }

  private configureBreadcrumbs(): void {
    if (this.orderId) {
      this.breadcrumbsData.title = 'order-summary.summary-edit';
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons[1]) {
        this.breadcrumbsData.buttons[1].label = 'button.confirm-edit-request';
      }
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons[0]) {
        delete this.breadcrumbsData.buttons[0].router;
        this.breadcrumbsData.buttons[0].do = () => {
          localStorage.removeItem('costCenter');
          localStorage.setItem('fromSummary', "true");
          this.router.navigate(['/new-order', this.orderId]);
        };
      }
    } else {
      this.breadcrumbsData.title = 'order-summary.summary';
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons[1]) {
        this.breadcrumbsData.buttons[1].label = 'button.confirm-request';
      }
      if (this.breadcrumbsData.buttons && this.breadcrumbsData.buttons[0]) {
        delete this.breadcrumbsData.buttons[0].router;
        this.breadcrumbsData.buttons[0].do = () => {
          localStorage.removeItem('costCenter');
          localStorage.setItem('fromSummary', "true");
          this.router.navigate(['/new-order']);
        };
      }
    }
  }

  private setupFormListeners(): void {
    this.subscriptions.add(
      this.projectDescription.valueChanges.subscribe(value => {
        localStorage.setItem('projectDescription', value);
        this.updateConfirmButtonState();
      })
    );

      this.costCenter.valueChanges.subscribe(value => {
        localStorage.setItem('costCenter', value);
        this.updateConfirmButtonState();
      });

    this.subscriptions.add(
      this.projectType.valueChanges.subscribe(value => {
        localStorage.setItem('projectType', value);
        this.updateConfirmButtonState();
      })
    );

    this.subscriptions.add(
      this.summaryForm.valueChanges.subscribe(value => {
        localStorage.setItem('remarks', value);
      })
    );
  }

  confirmDiscard(): void {
    localStorage.removeItem('costCenter');
    localStorage.setItem('fromSummary', "true");
    this.router.navigate(['/new-order', this.orderId]);
  }

  confirmOrderRequest() {
    const loader = this.dialog.open(LoaderDialogComponent, {
      disableClose: true,
    });
    const data: OrderForm = {
      items: this.cartItems.map(item => ({
        id: item.id,
        item: {
          id: item.id,
          description: item.description,
          partNumber: item.partNumber,
        },
        requestedQuantity: item.requestedQuantity,
        availableQuantity: item.availableQuantity,
        unitValue: item.unitValue,
        kit: item.kit ? {
          id: item.kit.id,
          description: item.kit.description,
        } : null
      })),
      remarks: this.summaryForm.value,
      projectDescription: this.projectDescription.value,
      projectType: this.projectType.value,
      costCenter: this.costCenter.value
    };

    if (this.orderId) {
        const message = this.translate.instant('order-summary.order-edit-successfull', {
          orderId: this.orderId,
          projectDescription: this.projectDescription.value
        });

      this.ordersService.updateOrder(this.orderId, data)
      .pipe(finalize(() => loader.close()))
      .subscribe({
        next: () => {
          this.removeLocalStorage();

          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: message,
              title: this.translate.instant('alert.information')
            },
          });

          this.router.navigate(['/orders-dashboard']);
        },
        error: () => {}
      });
    } else {
      this.ordersService.createOrder(data)
      .pipe(finalize(() => loader.close()))
      .subscribe({
        next: (orderId) => {
          this.removeLocalStorage();
          this.dialog.open(AlertComponent, {
            disableClose: true,
            data: {
              message: 'order-summary.order-creation-successfull',
              param: {orderId: orderId, projectDescription: this.projectDescription.value},
              title: 'alert.information',
            },
          });
          this.router.navigate(['/orders-dashboard']);
        },
        error: () => {}
      });
    }
  }

  removeItem(item: any): void {
    const index = this.cartItems.findIndex(ci => ci.id === item.id);
    if (index !== -1) {
      this.cartItems.splice(index, 1);
      this.updateLocalStorage();
      this.cartItems = [...this.cartItems];
    }
  }

  updateQuantity(item: any): void {
    this.updateLocalStorage();
  }

  private updateLocalStorage(): void {
    localStorage.setItem('cartItems', JSON.stringify(this.cartItems));
    this.totalQuantity = this.cartItems.reduce((sum: number, item: any) => sum + (item.requestedQuantity || 0), 0);
    localStorage.setItem('totalQuantity', this.totalQuantity.toString());
    this.updateConfirmButtonState();
  }

  blockInvalidCharacters(event: KeyboardEvent): void {
    if (event.key === ',' || event.key === '.' || event.key === '-' || event.key === '+') {
      event.preventDefault();
    }
  }

  blockInvalidPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedText = clipboardData.getData('text');
    if (!/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

  removeLocalStorage() {
    this.localStorageService.clearSummary();
  }
}
